import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { protestService, getImageUrl } from '../services/api';
import { useAuthStore } from '../store/useAuthStore';
import EditProtestForm from './EditProtestForm';
import styles from './ProtestList.module.css';

const PAGE_SIZES = { cards: 12, list: 20 };

function formatDate(str) {
    if (!str) return '—';
    return new Date(str).toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric' });
}

function Pagination({ page, pageCount, onChange }) {
    if (pageCount <= 1) return null;

    const pages = [];
    for (let i = 1; i <= pageCount; i++) {
        if (i === 1 || i === pageCount || (i >= page - 2 && i <= page + 2)) {
            pages.push(i);
        } else if (pages[pages.length - 1] !== '…') {
            pages.push('…');
        }
    }

    return (
        <div className={styles.pagination}>
            <button className={styles.pageBtn} onClick={() => onChange(page - 1)} disabled={page === 1}>‹</button>
            {pages.map((p, i) =>
                p === '…'
                    ? <span key={`e${i}`} className={styles.pageEllipsis}>…</span>
                    : <button
                        key={p}
                        className={`${styles.pageBtn} ${p === page ? styles.pageBtnActive : ''}`}
                        onClick={() => onChange(p)}
                      >{p}</button>
            )}
            <button className={styles.pageBtn} onClick={() => onChange(page + 1)} disabled={page === pageCount}>›</button>
        </div>
    );
}

export default function ProtestList({ refresh }) {
    const [protests, setProtests] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [view, setView]             = useState('cards');
    const [page, setPage]             = useState(1);
    const [sort, setSort]             = useState({ field: 'protest_date', dir: 'desc' });
    const user     = useAuthStore((s) => s.user);
    const navigate = useNavigate();

    useEffect(() => {
        setLoading(true);
        protestService.list()
            .then((pr) => setProtests(pr.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [refresh]);

    const switchView = (v) => { setView(v); setPage(1); setEditingId(null); };

    const toggleSort = (field) => {
        setSort((s) => ({ field, dir: s.field === field && s.dir === 'asc' ? 'desc' : 'asc' }));
        setPage(1);
    };

    const sortKey = (p) => {
        switch (sort.field) {
            case 'region':       return p.region?.name       ?? '';
            case 'governorate':  return p.governorate?.name  ?? '';
            case 'protest_type': return p.protest_type       ?? '';
            case 'protest_date': return p.protest_date       ?? '';
            default:             return '';
        }
    };

    const sorted = [...protests].sort((a, b) => {
        const av = sortKey(a), bv = sortKey(b);
        const cmp = av.localeCompare(bv, 'ar');
        return sort.dir === 'asc' ? cmp : -cmp;
    });

    const pageSize  = PAGE_SIZES[view];
    const pageCount = Math.ceil(sorted.length / pageSize);
    const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

    const SortIcon = ({ field }) => {
        if (sort.field !== field) return <span className={styles.sortIcon}>↕</span>;
        return <span className={`${styles.sortIcon} ${styles.sortActive}`}>{sort.dir === 'asc' ? '↑' : '↓'}</span>;
    };

    const canEdit   = (p) => user?.role !== 'monitor' && (user?.role === 'free_user' || user?.role === 'super_admin' || user?.id === p.user_id);
    const canDelete = (p) => user?.role !== 'monitor' && (user?.role === 'super_admin' || user?.id === p.user_id);

    const cancelEdit = () => setEditingId(null);

    const handleSaved = (id, updated) => {
        setProtests((prev) => prev.map((p) => (p.id === id ? { ...p, ...updated } : p)));
        cancelEdit();
    };

    const handleDelete = async (id) => {
        if (!window.confirm('هل أنت متأكد من الحذف؟')) return;
        await protestService.delete(id);
        setProtests((prev) => prev.filter((p) => p.id !== id));
    };

    const photoSrc = (p) => {
        if (p.photos?.length > 0) return p.photos[0].photo_url;
        if (p.photo_path) return getImageUrl(p.photo_path);
        return null;
    };

    if (loading) return <div className={`${styles.state} text-ar`}>جاري التحميل...</div>;
    if (!protests.length) return <div className={`${styles.state} ${styles.empty} text-ar`}>لا توجد تظاهرات مسجلة حتى الآن</div>;

    const start = (page - 1) * pageSize + 1;
    const end   = Math.min(page * pageSize, sorted.length);

    return (
        <div className={`${styles.wrap} text-ar`}>

            {/* Toolbar */}
            <div className={styles.toolbar}>
                <span className={styles.count}>
                    {sorted.length} تظاهرة — عرض {start}–{end}
                </span>
                <div className={styles.toolbarControls}>
                    <select
                        className={styles.sortSelect}
                        value={`${sort.field}:${sort.dir}`}
                        onChange={(e) => {
                            const [field, dir] = e.target.value.split(':');
                            setSort({ field, dir });
                            setPage(1);
                        }}
                    >
                        <option value="protest_date:desc">التاريخ — الأحدث</option>
                        <option value="protest_date:asc">التاريخ — الأقدم</option>
                        <option value="region:asc">المنطقة — أ→ي</option>
                        <option value="region:desc">المنطقة — ي→أ</option>
                        <option value="governorate:asc">المحافظة — أ→ي</option>
                        <option value="governorate:desc">المحافظة — ي→أ</option>
                        <option value="protest_type:asc">النوع — أ→ي</option>
                        <option value="protest_type:desc">النوع — ي→أ</option>
                    </select>
                <div className={styles.viewToggle}>
                    <button
                        className={`${styles.viewBtn} ${view === 'cards' ? styles.viewBtnActive : ''}`}
                        onClick={() => switchView('cards')}
                        title="عرض بطاقات"
                    >
                        ⊞
                    </button>
                    <button
                        className={`${styles.viewBtn} ${view === 'list' ? styles.viewBtnActive : ''}`}
                        onClick={() => switchView('list')}
                        title="عرض قائمة"
                    >
                        ☰
                    </button>
                </div>
                </div>
            </div>

            {/* Cards view */}
            {view === 'cards' && (
                <div className={styles.grid}>
                    {paginated.map((p) => (
                        <div key={p.id} className={`${styles.card} ${editingId === p.id ? styles.cardActive : ''}`}>
                            {(p.photos?.length > 0 || p.photo_path) && (
                                <div className={styles.photoWrap}>
                                    <img src={photoSrc(p)} alt="protest" className={styles.photo} />
                                    {p.photos?.length > 1 && (
                                        <span className={styles.photoCount}>+{p.photos.length - 1}</span>
                                    )}
                                </div>
                            )}
                            <div className={styles.cardBody}>
                                <div className={styles.cardHeader}>
                                    <div className={styles.titleGroup}>
                                        <h3 className={styles.location}>
                                            {p.governorate?.name
                                                ? p.region?.name
                                                    ? `${p.governorate.name} - ${p.region.name}`
                                                    : p.governorate.name
                                                : p.region?.name || 'غير محدد'}
                                        </h3>
                                        <div className={styles.cardMeta}>
                                            {p.protest_type && <span>{p.protest_type}</span>}
                                        </div>
                                        <p className={styles.date}>{formatDate(p.protest_date)}</p>
                                    </div>
                                    <div className={styles.cardActions}>
                                        <button
                                            className={styles.viewBtn2}
                                            onClick={() => navigate(`/protests/${p.id}`)}
                                            title="عرض التفاصيل"
                                        >↗</button>
                                        {p.latitude && p.longitude && (
                                            <a href={`https://www.google.com/maps?q=${p.latitude},${p.longitude}`}
                                                target="_blank" rel="noopener noreferrer"
                                                className={styles.mapBtn} title={`${p.latitude}, ${p.longitude}`}>📍</a>
                                        )}
                                        {canEdit(p) && (
                                            <button
                                                className={editingId === p.id ? styles.cancelBtn : styles.editBtn}
                                                onClick={() => editingId === p.id ? cancelEdit() : setEditingId(p.id)}
                                            >{editingId === p.id ? '✕' : '✏'}</button>
                                        )}
                                        {canDelete(p) && editingId !== p.id && (
                                            <button className={styles.deleteBtn} onClick={() => handleDelete(p.id)}>🗑</button>
                                        )}
                                    </div>
                                </div>
                                <div className={styles.chips}>
                                    {p.estimated_participants > 0 && (
                                        <span className={`${styles.chip} ${styles.chipCount}`}>
                                            {p.estimated_participants.toLocaleString()} مشارك
                                        </span>
                                    )}
                                </div>
                                {p.category && (
                                    <div className={styles.tags}>
                                        <span className={styles.tag}>{p.category.name}</span>
                                        {p.sub_category && (
                                            <span className={`${styles.tag} ${styles.tagSub}`}>{p.sub_category.name}</span>
                                        )}
                                    </div>
                                )}
                                {p.demands && <p className={styles.demands}>{p.demands}</p>}
                            </div>
                            {editingId === p.id && (
                                <div className={styles.editPanel}>
                                    <EditProtestForm
                                        protest={protests.find((x) => x.id === editingId)}
                                        onSave={(updated) => handleSaved(p.id, updated)}
                                        onCancel={cancelEdit}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* List view */}
            {view === 'list' && (
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th className={styles.th} />
                                <th className={`${styles.th} ${styles.thSort}`} onClick={() => toggleSort('protest_date')}>
                                    التاريخ <SortIcon field="protest_date" />
                                </th>
                                <th className={`${styles.th} ${styles.thSort}`} onClick={() => toggleSort('governorate')}>
                                    المحافظة - المنطقة <SortIcon field="governorate" />
                                </th>
                                <th className={`${styles.th} ${styles.thSort}`} onClick={() => toggleSort('protest_type')}>
                                    النوع <SortIcon field="protest_type" />
                                </th>
                                <th className={styles.th}>المشاركون</th>
                                <th className={styles.th}>التصنيف</th>
                                <th className={styles.th}>المطالب</th>
                                <th className={styles.th} />
                            </tr>
                        </thead>
                        <tbody>
                            {paginated.map((p) => (
                                <React.Fragment key={p.id}>
                                    <tr className={`${styles.tr} ${editingId === p.id ? styles.trActive : ''}`}>
                                        <td className={styles.td}>
                                            {photoSrc(p)
                                                ? <img src={photoSrc(p)} alt="" className={styles.listThumb} />
                                                : <div className={styles.listThumbEmpty} />}
                                        </td>
                                        <td className={styles.td}><span className={styles.tdDate}>{formatDate(p.protest_date)}</span></td>
                                        <td className={styles.td}>
                                            <span className={styles.tdBold}>
                                                {p.governorate?.name
                                                    ? p.region?.name
                                                        ? `${p.governorate.name} - ${p.region.name}`
                                                        : p.governorate.name
                                                    : p.region?.name || '—'}
                                            </span>
                                        </td>
                                        <td className={styles.td}><span className={styles.tdMuted}>{p.protest_type || '—'}</span></td>
                                        <td className={styles.td}>
                                            {p.estimated_participants > 0
                                                ? <span className={styles.tdCount}>{p.estimated_participants.toLocaleString()}</span>
                                                : <span className={styles.tdMuted}>—</span>}
                                        </td>
                                        <td className={styles.td}>
                                            {p.category
                                                ? <div className={styles.tdCat}>
                                                    <span className={styles.tag}>{p.category.name}</span>
                                                    {p.sub_category && <span className={`${styles.tag} ${styles.tagSub}`}>{p.sub_category.name}</span>}
                                                  </div>
                                                : <span className={styles.tdMuted}>—</span>}
                                        </td>
                                        <td className={styles.td}><span className={styles.tdDemands}>{p.demands || '—'}</span></td>
                                        <td className={styles.td}>
                                            <div className={styles.cardActions}>
                                                <button
                                                    className={styles.viewBtn2}
                                                    onClick={() => navigate(`/protests/${p.id}`)}
                                                    title="عرض التفاصيل"
                                                >↗</button>
                                                {p.latitude && p.longitude && (
                                                    <a href={`https://www.google.com/maps?q=${p.latitude},${p.longitude}`}
                                                        target="_blank" rel="noopener noreferrer"
                                                        className={styles.mapBtn}>📍</a>
                                                )}
                                                {canEdit(p) && (
                                                    <button
                                                        className={editingId === p.id ? styles.cancelBtn : styles.editBtn}
                                                        onClick={() => editingId === p.id ? cancelEdit() : setEditingId(p.id)}
                                                    >{editingId === p.id ? '✕' : '✏'}</button>
                                                )}
                                                {canDelete(p) && editingId !== p.id && (
                                                    <button className={styles.deleteBtn} onClick={() => handleDelete(p.id)}>🗑</button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {editingId === p.id && (
                                        <tr>
                                            <td colSpan={8} className={styles.tdEditPanel}>
                                                <EditProtestForm
                                                    protest={protests.find((x) => x.id === editingId)}
                                                    onSave={(updated) => handleSaved(p.id, updated)}
                                                    onCancel={cancelEdit}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Pagination page={page} pageCount={pageCount} onChange={setPage} />
        </div>
    );
}
